import maleFigure from '../../assets/images/body/silhouette-male.png';
import femaleFigure from '../../assets/images/body/silhouette-female.png';
import maleBackFigure from '../../assets/images/body/silhouette-male-back.png';
import femaleBackFigure from '../../assets/images/body/silhouette-female-back.png';

/**
 * Figuras da silhueta (frente e costas) e o ponto de cada região medível.
 *
 * As imagens vivem em `assets/images/body/` (PNG com fundo transparente,
 * recortadas no mesmo quadro de 358 x 960 para que etiquetas e marcadores
 * usem a mesma escala nas duas vistas). As coordenadas abaixo estão nesse quadro, medidas sobre as bordas
 * do corpo; as chaves são as do catálogo de medidas (`bodyMeasures.js`) e o
 * lado direito do paciente fica à esquerda de quem olha.
 *
 * Para trocar uma figura: recorte a nova imagem no mesmo quadro e confira os
 * pontos na tela. Sem sexo no cadastro vale a figura masculina.
 */

export const BODY_SILHOUETTE_VIEWBOX = Object.freeze({ width: 358, height: 960 });

const MALE_REGIONS = {
  neck: { x: 179, y: 150 },
  chest: { x: 179, y: 238 },
  waist: { x: 179, y: 343 },
  abdomen: { x: 179, y: 402 },
  hip: { x: 179, y: 468 },
  arm_right: { x: 72, y: 290 },
  arm_left: { x: 284, y: 290 },
  thigh_right: { x: 128, y: 567 },
  thigh_left: { x: 229, y: 567 },
  calf_right: { x: 119, y: 732 },
  calf_left: { x: 238, y: 732 },
};

const FEMALE_REGIONS = {
  neck: { x: 179, y: 150 },
  chest: { x: 179, y: 238 },
  waist: { x: 179, y: 340 },
  abdomen: { x: 179, y: 402 },
  hip: { x: 179, y: 472 },
  arm_right: { x: 85, y: 290 },
  arm_left: { x: 272, y: 290 },
  thigh_right: { x: 133, y: 567 },
  thigh_left: { x: 224, y: 567 },
  calf_right: { x: 125, y: 732 },
  calf_left: { x: 231, y: 732 },
};

const FIGURES = {
  male: { src: maleFigure, backSrc: maleBackFigure, regions: MALE_REGIONS },
  female: { src: femaleFigure, backSrc: femaleBackFigure, regions: FEMALE_REGIONS },
  neutral: { src: maleFigure, backSrc: maleBackFigure, regions: MALE_REGIONS },
};

export const BODY_SILHOUETTE_VARIANTS = Object.freeze(Object.keys(FIGURES));

/** Sexo do cadastro (PatientConfig.Genders: "0" masculino, "1" feminino) para a variante. */
export function variantFromGender(gender) {
  if (gender === '0' || gender === 0) return 'male';
  if (gender === '1' || gender === 1) return 'female';
  return 'neutral';
}

/** Imagens (frente e costas) e pontos de região de uma variante. */
export function getBodySilhouetteFigure(variant) {
  return FIGURES[variant] || FIGURES.neutral;
}

/**
 * Distribui as etiquetas de um lado para não se sobreporem: ordena por altura
 * e empurra para baixo quem ficaria a menos de `minGap` da anterior, mantendo
 * tudo dentro do viewBox. Retorna cópias com `labelY`.
 */
export function spreadLabels(items, minGap = 92) {
  const sorted = [...items].sort((a, b) => a.y - b.y).map((item) => ({ ...item, labelY: item.y }));
  for (let i = 1; i < sorted.length; i += 1) {
    const minY = sorted[i - 1].labelY + minGap;
    if (sorted[i].labelY < minY) sorted[i].labelY = minY;
  }
  const maxY = BODY_SILHOUETTE_VIEWBOX.height - minGap / 2;
  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    const limit = i === sorted.length - 1 ? maxY : sorted[i + 1].labelY - minGap;
    if (sorted[i].labelY > limit) sorted[i].labelY = limit;
  }
  return sorted;
}
